<template lang="pug">
v-menu(v-model="showMenu" offset-y rounded="lg" :min-width="block ? undefined : '300'" :max-width="block ? undefined : '300'" :close-on-content-click="false" left max-height="444" nudge-bottom="4")
  template(v-slot:activator="{ on }")
    yz-btn(v-on="on" :label="displayText" outlined size="lg" background-color="white" append-icon="$yzi-chevron-down" :block="block" :button-class="block ? 'branch-dropdown--block' : null")
  .white
    v-skeleton-loader(v-if="loading" type="list-item-three-line" width="200")
    div(v-else)
      .sticky-search.white.pa-2
        v-text-field.round-xl(v-model="searchQuery" placeholder="Search branch or warehouse..." hide-details dense outlined clearable)
      .scrollable-content(style="max-height: 300px; overflow-y: auto;")
        template(v-if="multiple")
          //- Select All (multiple only)
          .hover-bg(v-if="!searchQuery" @click="onToggleSelectAll")
            .d-flex.align-center.justify-space-between.px-4.py-3
              .yz-regular-body-sm.font-weight-medium All branches & warehouses
              v-checkbox(:input-value="isAllSelected" :indeterminate="isIndeterminate" hide-details dense @click.stop="onToggleSelectAll")
            v-divider.basic300
          //- Branches group
          template(v-if="filteredBranches.length")
            .hover-bg(v-if="!searchQuery" @click="onToggleGroupSelectAll('branch')")
              .d-flex.align-center.justify-space-between.px-4.py-2
                .yz-regular-body-sm.font-weight-medium.neutral500--text Branches
                v-checkbox(:input-value="isGroupAllSelected('branch')" :indeterminate="isGroupIndeterminate('branch')" hide-details dense @click.stop="onToggleGroupSelectAll('branch')")
            .hover-bg(v-for="item in filteredBranches" :key="item.id" @click="onToggleItem(item)")
              .d-flex.align-center.justify-space-between.px-4.py-3
                .yz-regular-body-sm {{ item.name }}
                v-checkbox(:input-value="isItemSelected(item)" hide-details dense @click.stop="onToggleItem(item)")
              v-divider.basic300
          //- Warehouses group
          template(v-if="filteredWarehouses.length")
            .hover-bg(v-if="!searchQuery" @click="onToggleGroupSelectAll('warehouse')")
              .d-flex.align-center.justify-space-between.px-4.py-2
                .yz-regular-body-sm.font-weight-medium.neutral500--text Warehouses
                v-checkbox(:input-value="isGroupAllSelected('warehouse')" :indeterminate="isGroupIndeterminate('warehouse')" hide-details dense @click.stop="onToggleGroupSelectAll('warehouse')")
            .hover-bg(v-for="item in filteredWarehouses" :key="item.id" @click="onToggleItem(item)")
              .d-flex.align-center.justify-space-between.px-4.py-3
                .yz-regular-body-sm {{ item.name }}
                v-checkbox(:input-value="isItemSelected(item)" hide-details dense @click.stop="onToggleItem(item)")
              v-divider.basic300
        yz-radio-group(v-else :value="selectedId" direction="vertical" :spacing="0" @input="onSelectItemById")
          //- Branches group (single select)
          template(v-if="filteredBranches.length")
            .px-4.py-2
              .yz-regular-body-sm.font-weight-medium.neutral500--text Branches
            .hover-bg(v-for="item in filteredBranches" :key="item.id" @click="onSelectItem(item)")
              .d-flex.align-center.px-4.py-3
                yz-radio(:value="item.id" :label="item.name")
              v-divider.basic300
          //- Warehouses group (single select)
          template(v-if="filteredWarehouses.length")
            .px-4.py-2
              .yz-regular-body-sm.font-weight-medium.neutral500--text Warehouses
            .hover-bg(v-for="item in filteredWarehouses" :key="item.id" @click="onSelectItem(item)")
              .d-flex.align-center.px-4.py-3
                yz-radio(:value="item.id" :label="item.name")
              v-divider.basic300
        //- No results
        template(v-if="!filteredBranches.length && !filteredWarehouses.length")
          .px-4.py-3.yz-regular-body-sm.neutral500--text No branch or warehouse found
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  props: {
    value: { default: null },
    block: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false }
  },
  data() {
    return {
      showMenu: false,
      allItems: [],
      loading: false,
      searchQuery: ''
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    companyId() {
      return this.company?.branch_of?.id || this.company?.id
    },
    selectedId() {
      if (!this.multiple && this.value) return this.value.id
      return null
    },
    branches() {
      return this.allItems.filter(i => i.type === 'branch')
    },
    warehouses() {
      return this.allItems.filter(i => i.type === 'warehouse')
    },
    filteredBranches() {
      if (!this.searchQuery || this.searchQuery.trim() === '') return this.branches
      const q = this.searchQuery.toLowerCase().trim()
      return this.branches.filter(i => i.name?.toLowerCase().includes(q))
    },
    filteredWarehouses() {
      if (!this.searchQuery || this.searchQuery.trim() === '') return this.warehouses
      const q = this.searchQuery.toLowerCase().trim()
      return this.warehouses.filter(i => i.name?.toLowerCase().includes(q))
    },
    displayText() {
      if (this.multiple) {
        if (!this.value || this.value.length === this.allItems.length) return 'All branches & warehouses'
        if (this.value.length === 0) return 'No selection'
        if (this.value.length === 1) return this.value[0].name
        const branchCount = this.value.filter(v => v.type === 'branch').length
        const warehouseCount = this.value.filter(v => v.type === 'warehouse').length
        const parts = []
        if (branchCount > 0) parts.push(`${branchCount} ${branchCount === 1 ? 'branch' : 'branches'}`)
        if (warehouseCount > 0) parts.push(`${warehouseCount} ${warehouseCount === 1 ? 'warehouse' : 'warehouses'}`)
        return parts.join(', ')
      }
      if (!this.value) return 'Select destination'
      return this.value.name
    },
    isAllSelected() {
      return this.allItems.length > 0 && (this.value === null || this.value.length === this.allItems.length)
    },
    isIndeterminate() {
      return this.value !== null && this.value.length > 0 && this.value.length < this.allItems.length
    }
  },
  watch: {
    showMenu(val, oldVal) {
      if (!val && oldVal) {
        this.searchQuery = ''
      }
    }
  },
  mounted() {
    this.fetchBranchesAndWarehouses()
  },
  methods: {
    async fetchBranchesAndWarehouses() {
      this.loading = true
      try {
        const resp = await this.$axios.get(`/companies/${this.companyId}/branches`, { params: { page_size: -1 } })
        const results = resp.data?.results || []
        this.allItems = results.map(item => ({
          id: item.id,
          slug: item.slug,
          name: item.name || item.company_name,
          type: item.os === 'warehouse' ? 'warehouse' : 'branch'
        }))
      } catch {
        this.allItems = []
      } finally {
        this.loading = false
      }
    },
    // Single select
    onSelectItem(item) {
      this.$emit('input', item)
      this.showMenu = false
    },
    onSelectItemById(id) {
      const item = this.allItems.find(i => i.id === id)
      if (item) this.onSelectItem(item)
    },
    // Multiple select methods
    isItemSelected(item) {
      return this.value === null || this.value.some(v => v.id === item.id)
    },
    isGroupAllSelected(type) {
      const groupItems = type === 'branch' ? this.branches : this.warehouses
      if (groupItems.length === 0) return false
      const selected = this.value === null ? this.allItems : this.value
      return groupItems.every(item => selected.some(v => v.id === item.id))
    },
    isGroupIndeterminate(type) {
      const groupItems = type === 'branch' ? this.branches : this.warehouses
      if (groupItems.length === 0) return false
      const selected = this.value === null ? this.allItems : this.value
      const selectedCount = groupItems.filter(item => selected.some(v => v.id === item.id)).length
      return selectedCount > 0 && selectedCount < groupItems.length
    },
    onToggleItem(item) {
      const current = this.value === null ? [...this.allItems] : [...this.value]
      const index = current.findIndex(v => v.id === item.id)
      if (index > -1) {
        current.splice(index, 1)
      } else {
        current.push(item)
      }
      this.$emit('input', current.length === this.allItems.length ? null : current)
    },
    onToggleSelectAll() {
      if (this.isAllSelected) {
        this.$emit('input', [])
      } else {
        this.$emit('input', null)
      }
    },
    onToggleGroupSelectAll(type) {
      const groupItems = type === 'branch' ? this.branches : this.warehouses
      const current = this.value === null ? [...this.allItems] : [...this.value]
      const allGroupSelected = this.isGroupAllSelected(type)
      let updated
      if (allGroupSelected) {
        updated = current.filter(v => v.type !== type)
      } else {
        const currentWithoutGroup = current.filter(v => v.type !== type)
        updated = [...currentWithoutGroup, ...groupItems]
      }
      this.$emit('input', updated.length === this.allItems.length ? null : updated)
    }
  }
}
</script>

<style lang="scss" scoped>
.sticky-search {
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
  position: sticky;
  top: 0;
  z-index: 1;
}

.hover-bg:hover {
  background-color: var(--v-neutral200) !important;
  cursor: pointer;
}

.branch-dropdown--block ::v-deep .yz-btn__content {
  justify-content: space-between;
  width: 100%;
}
</style>
